import React from "react";
import { Link, useParams } from "react-router-dom";
import Reveal from "../components/site/Reveal";
import { useBlogPost } from "../lib/useBlog";
import { BLOG } from "../constants/testIds";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useSeo, blogPostingSchema } from "../lib/seo";
import { BOOKING_URL } from "../lib/site";

// Render a tiny inline markdown for **bold** within paragraph text
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, idx) => {
    const key = `${idx}-${p.slice(0, 24)}`;
    if (/^\*\*[^*]+\*\*$/.test(p)) {
      return <strong key={key}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={key}>{p}</React.Fragment>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const { post, loading, error } = useBlogPost(slug);

  useSeo(
    post
      ? {
          title: `${post.title}. Alae Dental Journal`,
          description: post.meta_description,
          path: `/blog/${post.slug}`,
          type: "article",
          jsonLd: blogPostingSchema(post),
          jsonLdId: "blogpost-jsonld",
        }
      : { title: "" }
  );

  if (loading) {
    return (
      <main className="bg-linen text-umber min-h-[60vh] flex items-center justify-center">
        <p className="font-meta text-umber/60">Loading article…</p>
      </main>
    );
  }

  if (error || !post) {
    return (
      <main className="bg-linen text-umber min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display text-4xl text-umber">Article not found</h1>
        <Link
          to="/blog"
          className="mt-8 inline-flex items-center gap-2 font-meta text-forest"
        >
          <ArrowLeft size={14} /> Back to the Journal
        </Link>
      </main>
    );
  }

  const paragraphs = post.body.split(/\n\n+/);
  const publishedDate = new Date(post.published_at).toLocaleDateString(
    "en-US",
    { month: "long", day: "numeric", year: "numeric" }
  );

  return (
    <main className="bg-linen text-umber" data-testid={BLOG.post}>
      <article className="pt-24 md:pt-32 pb-20 md:pb-28 paper-grain">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-meta text-umber/70 hover:text-forest transition-colors"
          >
            <ArrowLeft size={14} /> Back to the Journal
          </Link>

          <Reveal>
            <div className="font-meta text-forest mt-12 mb-6">
              {publishedDate} · {post.author}
            </div>
          </Reveal>

          <Reveal delay={100}>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-umber">
              {post.title}
            </h1>
          </Reveal>

          <Reveal delay={200}>
            <p className="mt-8 font-display-italic text-xl md:text-2xl leading-relaxed text-umber/75">
              {post.meta_description}
            </p>
          </Reveal>

          <div className="my-14 h-px bg-umber/20" />

          <Reveal delay={250}>
            <div className="prose-alae has-dropcap">
              {paragraphs.map((para) => (
                <p key={para.slice(0, 40)}>{renderInline(para)}</p>
              ))}
            </div>
          </Reveal>

          <div className="mt-16 pt-10 border-t border-umber/20">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-forest text-paper rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-all duration-300"
            >
              Book an Appointment <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </article>
    </main>
  );
}
